import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Users, Plus, Edit2, Trash2, Check, Loader2, AlertTriangle, Search, Shield, Eye, Server, Globe, FileText, Activity, ChevronDown, ChevronUp, X, RefreshCw, FolderOpen, Settings } from 'lucide-react';

interface User {
    id: number;
    username: string;
    role: string;
    is_active: number;
    servers_limit: number | null;
    domains_limit: number | null;
    subscription_expires: string | null;
    created_at: string;
    last_login: string | null;
    servers_count: number;
    domains_count: number;
}

interface UserFile {
    name: string;
    size: number;
    modified: string;
    extension: string;
}

interface UserDetails {
    user: User;
    servers: Array<{
        id: number;
        name: string;
        host: string;
        status: string;
        created_at: string;
        pages_count: number;
        monitoring_rules_count: number;
    }>;
    domains: Array<{
        id: number;
        domain: string;
        status: string;
        auto_ssl: number;
        dns_provider: string;
        created_at: string;
    }>;
    pages: Array<{
        id: number;
        name: string;
        url: string;
        server_id: number;
        server_name: string;
        created_at: string;
    }>;
    monitoringRules: Array<{
        id: number;
        name: string;
        type: string;
        is_active: number;
        servers: string;
        created_at: string;
    }>;
    settings: Record<string, any>;
    files?: UserFile[];
}

const ROLES = ['user', 'starter', 'pro', 'enterprise', 'admin'];

const ROLE_COLORS: Record<string, string> = {
    user: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    starter: 'bg-green-500/20 text-green-400 border-green-500/30',
    pro: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    enterprise: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    admin: 'bg-red-500/20 text-red-400 border-red-500/30'
};

const UsersPanel: React.FC = () => {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [saving, setSaving] = useState(false);
    const [viewingUser, setViewingUser] = useState<UserDetails | null>(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        servers: true,
        domains: true,
        pages: false,
        monitoring: false,
        files: false,
        settings: false
    });

    const [formData, setFormData] = useState({
        username: '',
        password: '',
        role: 'user',
        servers_limit: '',
        domains_limit: '',
        subscription_expires: ''
    });

    // Transfer modal state
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [transferFromId, setTransferFromId] = useState<number | null>(null);
    const [transferToId, setTransferToId] = useState<string>('');
    const [transferring, setTransferring] = useState(false);

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch('/api/users', { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                setUsers(data.users);
            } else {
                setError(data.error || 'Failed to load users');
            }
        } catch (err) {
            console.error('Error loading users:', err);
            setError('Failed to connect to server');
        } finally {
            setLoading(false);
        }
    };

    const loadUserDetails = async (userId: number) => {
        setLoadingDetails(true);
        try {
            const response = await fetch(`/api/users/${userId}`, { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                // Load user files
                try {
                    const filesResponse = await fetch(`/api/user-files/admin/${data.user.username}`, { credentials: 'include' });
                    const filesData = await filesResponse.json();
                    if (filesData.success) {
                        data.files = filesData.files;
                    }
                } catch (e) {
                    console.error('Error loading user files:', e);
                    data.files = [];
                }
                setViewingUser(data);
            } else {
                alert(data.error || 'Failed to load user details');
            }
        } catch (err) {
            console.error('Error loading user details:', err);
            alert('Failed to load user details');
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleCreate = async () => {
        if (!formData.username || !formData.password) {
            alert('Username and password are required');
            return;
        }
        setSaving(true);
        try {
            const response = await fetch('/api/users', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    username: formData.username,
                    password: formData.password,
                    role: formData.role,
                    servers_limit: formData.servers_limit || null,
                    domains_limit: formData.domains_limit || null,
                    subscription_expires: formData.subscription_expires || null
                })
            });
            const data = await response.json();
            if (data.success) {
                loadUsers();
                setShowCreateModal(false);
                resetForm();
            } else {
                alert(data.error || 'Failed to create user');
            }
        } catch (err) {
            console.error('Create user error:', err);
            alert('Failed to create user');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdate = async () => {
        if (!editingUser) return;
        setSaving(true);
        try {
            const updateData: any = {
                username: formData.username,
                role: formData.role,
                servers_limit: formData.servers_limit || null,
                domains_limit: formData.domains_limit || null,
                subscription_expires: formData.subscription_expires || null
            };
            if (formData.password) {
                updateData.password = formData.password;
            }
            const response = await fetch(`/api/users/${editingUser.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(updateData)
            });
            const data = await response.json();
            if (data.success) {
                await loadUsers();
                // Refresh viewing user if it's the same
                if (viewingUser && viewingUser.user.id === editingUser.id) {
                    loadUserDetails(editingUser.id);
                }
                setEditingUser(null);
                resetForm();
            } else {
                alert(data.error || 'Failed to update user');
            }
        } catch (err) {
            console.error('Update user error:', err);
            alert('Failed to update user');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (userId: number) => {
        if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
        try {
            const response = await fetch(`/api/users/${userId}`, { method: 'DELETE', credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                await loadUsers();
                // Clear viewing user if it's the deleted one
                if (viewingUser && viewingUser.user.id === userId) {
                    setViewingUser(null);
                }
            } else {
                alert(data.error || 'Failed to delete user');
            }
        } catch (err) {
            console.error('Delete user error:', err);
            alert('Failed to delete user');
        }
    };

    const handleToggleActive = async (userId: number) => {
        try {
            const response = await fetch(`/api/users/${userId}/toggle-active`, { method: 'POST', credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                await loadUsers();
                // Refresh viewing user if it's the same
                if (viewingUser && viewingUser.user.id === userId) {
                    loadUserDetails(userId);
                }
            }
        } catch (err) {
            console.error('Toggle active error:', err);
        }
    };

    const handleTransfer = async () => {
        if (!transferFromId || !transferToId) {
            alert('Select target user');
            return;
        }
        setTransferring(true);
        try {
            const response = await fetch('/api/users/transfer', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ fromUserId: transferFromId, toUserId: parseInt(transferToId) })
            });
            const data = await response.json();
            if (data.success) {
                alert(`Transferred: ${data.transferred.servers} servers, ${data.transferred.domains} domains, ${data.transferred.pages} pages`);
                setShowTransferModal(false);
                setTransferFromId(null);
                setTransferToId('');
                loadUsers();
                if (viewingUser) loadUserDetails(viewingUser.user.id);
            } else {
                alert(data.error || 'Failed to transfer');
            }
        } catch (err) {
            console.error('Transfer error:', err);
            alert('Failed to transfer');
        } finally {
            setTransferring(false);
        }
    };

    const openTransferModal = (userId: number) => {
        setTransferFromId(userId);
        setShowTransferModal(true);
    };

    const resetForm = () => {
        setFormData({ username: '', password: '', role: 'user', servers_limit: '', domains_limit: '', subscription_expires: '' });
    };

    const openEditModal = (user: User) => {
        setEditingUser(user);
        setFormData({
            username: user.username,
            password: '',
            role: user.role,
            servers_limit: user.servers_limit?.toString() || '',
            domains_limit: user.domains_limit?.toString() || '',
            subscription_expires: user.subscription_expires?.split('T')[0] || ''
        });
    };

    const toggleSection = (section: string) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const filteredUsers = users.filter(u => u.username.toLowerCase().includes(searchQuery.toLowerCase()));

    if (currentUser?.role !== 'admin') {
        return (
            <div className="w-full h-full p-8 flex items-center justify-center">
                <div className="text-center">
                    <AlertTriangle className="w-16 h-16 text-accent-red mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-text-primary mb-2">Access Denied</h2>
                    <p className="text-text-muted">Admin privileges required to access this page.</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="w-full h-full p-8 flex items-center justify-center">
                <div className="text-center space-y-4">
                    <Loader2 className="w-12 h-12 text-accent-blue animate-spin mx-auto" />
                    <p className="text-text-muted">Loading users...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full min-h-full animate-fade-in">
            <div className="flex gap-6">
                {/* Left Sidebar - User List */}
                <div className="w-72 flex-shrink-0">
                    {/* Header */}
                    <div className="mb-4">
                        <h1 className="text-2xl font-bold text-white">Users</h1>
                        <p className="text-sm text-white/40 mt-1">Manage accounts</p>
                    </div>

                    {/* Search + Add */}
                    <div className="space-y-3 mb-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:border-white/20"
                            />
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-white text-black rounded-lg text-sm font-medium hover:bg-white/90"
                            >
                                <Plus className="w-4 h-4" />
                                Add
                            </button>
                            <button
                                onClick={loadUsers}
                                className="p-2 text-white/50 hover:text-white hover:bg-white/5 rounded-lg"
                                title="Refresh"
                            >
                                <RefreshCw className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-400" />
                            <span className="text-red-400 text-xs">{error}</span>
                        </div>
                    )}

                    {/* Users List */}
                    <div className="space-y-1 max-h-[calc(100vh-280px)] overflow-y-auto">
                        {filteredUsers.length === 0 ? (
                            <div className="text-center py-8">
                                <Users className="w-8 h-8 text-white/20 mx-auto mb-2" />
                                <p className="text-white/40 text-sm">No users found</p>
                            </div>
                        ) : (
                            filteredUsers.map((user) => (
                                <button
                                    key={user.id}
                                    onClick={() => loadUserDetails(user.id)}
                                    className={`w-full text-left p-3 rounded-lg transition-colors ${viewingUser?.user.id === user.id
                                        ? 'bg-white/10 border-l-2 border-white'
                                        : 'hover:bg-white/5'
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${ROLE_COLORS[user.role]}`}>
                                            {user.role === 'admin' ? <Shield className="w-4 h-4" /> : <Users className="w-4 h-4" />}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium text-white truncate">{user.username}</p>
                                            <div className="flex items-center gap-2 text-xs text-white/40">
                                                <span className="capitalize">{user.role}</span>
                                                <span>•</span>
                                                <span>{user.servers_count}S / {user.domains_count}D</span>
                                            </div>
                                        </div>
                                        <div className={`w-2 h-2 rounded-full ${user.is_active ? 'bg-green-400' : 'bg-red-400'}`} />
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* Right Content - User Details */}
                <div className="flex-1 min-w-0">
                    {!viewingUser ? (
                        <div className="h-full flex items-center justify-center rounded-lg bg-white/[0.02] border border-white/10 min-h-[400px]">
                            <div className="text-center">
                                <Eye className="w-12 h-12 text-white/20 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-white mb-2">Select a user</h3>
                                <p className="text-white/40 text-sm">Click on a user to view details</p>
                            </div>
                        </div>
                    ) : loadingDetails ? (
                        <div className="h-full flex items-center justify-center rounded-lg bg-white/[0.02] border border-white/10 min-h-[400px]">
                            <div className="text-center">
                                <Loader2 className="w-8 h-8 animate-spin text-white/40 mx-auto mb-4" />
                                <p className="text-white/40">Loading details...</p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* User Header */}
                            <div className="p-5 rounded-lg bg-white/[0.02] border border-white/10">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${ROLE_COLORS[viewingUser.user.role]}`}>
                                            {viewingUser.user.role === 'admin' ? <Shield className="w-7 h-7" /> : <Users className="w-7 h-7" />}
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-bold text-white">{viewingUser.user.username}</h2>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className={`px-2 py-0.5 rounded text-xs capitalize border ${ROLE_COLORS[viewingUser.user.role]}`}>
                                                    {viewingUser.user.role}
                                                </span>
                                                <span className={`px-2 py-0.5 rounded text-xs ${viewingUser.user.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                                    {viewingUser.user.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => openEditModal(viewingUser.user)}
                                            className="p-2 rounded-lg bg-accent-green/10 hover:bg-accent-green/20 text-accent-green"
                                            title="Edit"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleToggleActive(viewingUser.user.id)}
                                            className={`p-2 rounded-lg ${viewingUser.user.is_active ? 'bg-yellow-500/10 text-yellow-400' : 'bg-green-500/10 text-green-400'}`}
                                            title={viewingUser.user.is_active ? 'Deactivate' : 'Activate'}
                                        >
                                            {viewingUser.user.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                                        </button>
                                        <button
                                            onClick={() => openTransferModal(viewingUser.user.id)}
                                            className="p-2 rounded-lg bg-orange-500/10 text-orange-400"
                                            title="Transfer Data"
                                        >
                                            <RefreshCw className="w-4 h-4" />
                                        </button>
                                        {viewingUser.user.id !== currentUser?.id && (
                                            <button
                                                onClick={() => handleDelete(viewingUser.user.id)}
                                                className="p-2 rounded-lg bg-accent-red/10 hover:bg-accent-red/20 text-accent-red"
                                                title="Delete"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Stats Grid */}
                            <div className="grid grid-cols-4 gap-3">
                                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/10 text-center">
                                    <Server className="w-6 h-6 text-accent-green mx-auto mb-2" />
                                    <p className="text-2xl font-bold text-white">{viewingUser.servers.length}</p>
                                    <p className="text-xs text-white/40">Servers</p>
                                </div>
                                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/10 text-center">
                                    <Globe className="w-6 h-6 mx-auto mb-2" style={{ color: 'var(--accent-primary)' }} />
                                    <p className="text-2xl font-bold text-white">{viewingUser.domains.length}</p>
                                    <p className="text-xs text-white/40">Domains</p>
                                </div>
                                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/10 text-center">
                                    <FileText className="w-6 h-6 text-accent-blue mx-auto mb-2" />
                                    <p className="text-2xl font-bold text-white">{viewingUser.pages.length}</p>
                                    <p className="text-xs text-white/40">Pages</p>
                                </div>
                                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/10 text-center">
                                    <Activity className="w-6 h-6 text-orange-400 mx-auto mb-2" />
                                    <p className="text-2xl font-bold text-white">{viewingUser.monitoringRules.length}</p>
                                    <p className="text-xs text-white/40">Rules</p>
                                </div>
                            </div>

                            {/* Expandable Sections */}
                            <div className="space-y-3">
                                {/* Servers */}
                                <div className="rounded-lg bg-white/[0.02] border border-white/10 overflow-hidden">
                                    <button
                                        onClick={() => toggleSection('servers')}
                                        className="w-full p-4 flex items-center justify-between hover:bg-white/5"
                                    >
                                        <div className="flex items-center gap-3">
                                            <Server className="w-5 h-5 text-accent-green" />
                                            <span className="font-medium text-white">Servers ({viewingUser.servers.length})</span>
                                        </div>
                                        {expandedSections.servers ? <ChevronUp className="w-5 h-5 text-white/40" /> : <ChevronDown className="w-5 h-5 text-white/40" />}
                                    </button>
                                    {expandedSections.servers && viewingUser.servers.length > 0 && (
                                        <div className="p-4 pt-0 space-y-2">
                                            {viewingUser.servers.map(s => (
                                                <div key={s.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                                                    <div>
                                                        <p className="text-sm font-medium text-white">{s.name}</p>
                                                        <p className="text-xs text-white/40">{s.host}</p>
                                                    </div>
                                                    <span className={`px-2 py-0.5 rounded text-xs ${s.status === 'online' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                                        {s.status}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Domains */}
                                <div className="rounded-lg bg-white/[0.02] border border-white/10 overflow-hidden">
                                    <button
                                        onClick={() => toggleSection('domains')}
                                        className="w-full p-4 flex items-center justify-between hover:bg-white/5"
                                    >
                                        <div className="flex items-center gap-3">
                                            <Globe className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
                                            <span className="font-medium text-white">Domains ({viewingUser.domains.length})</span>
                                        </div>
                                        {expandedSections.domains ? <ChevronUp className="w-5 h-5 text-white/40" /> : <ChevronDown className="w-5 h-5 text-white/40" />}
                                    </button>
                                    {expandedSections.domains && viewingUser.domains.length > 0 && (
                                        <div className="p-4 pt-0 space-y-2">
                                            {viewingUser.domains.map(d => (
                                                <div key={d.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                                                    <p className="text-sm font-medium text-white">{d.domain}</p>
                                                    <span className={`px-2 py-0.5 text-xs rounded ${d.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                                        {d.status}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Pages */}
                                <div className="rounded-lg bg-white/[0.02] border border-white/10 overflow-hidden">
                                    <button
                                        onClick={() => toggleSection('pages')}
                                        className="w-full p-4 flex items-center justify-between hover:bg-white/5"
                                    >
                                        <div className="flex items-center gap-3">
                                            <FileText className="w-5 h-5 text-accent-blue" />
                                            <span className="font-medium text-white">Pages ({viewingUser.pages.length})</span>
                                        </div>
                                        {expandedSections.pages ? <ChevronUp className="w-5 h-5 text-white/40" /> : <ChevronDown className="w-5 h-5 text-white/40" />}
                                    </button>
                                    {expandedSections.pages && viewingUser.pages.length > 0 && (
                                        <div className="p-4 pt-0 space-y-2 max-h-48 overflow-y-auto">
                                            {viewingUser.pages.map(p => (
                                                <div key={p.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-medium text-white truncate">{p.name}</p>
                                                        <p className="text-xs text-white/40 truncate">{p.url}</p>
                                                    </div>
                                                    <span className="text-xs text-white/40 flex-shrink-0 ml-2">{p.server_name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Monitoring */}
                                <div className="rounded-lg bg-white/[0.02] border border-white/10 overflow-hidden">
                                    <button
                                        onClick={() => toggleSection('monitoring')}
                                        className="w-full p-4 flex items-center justify-between hover:bg-white/5"
                                    >
                                        <div className="flex items-center gap-3">
                                            <Activity className="w-5 h-5 text-orange-400" />
                                            <span className="font-medium text-white">Monitoring ({viewingUser.monitoringRules.length})</span>
                                        </div>
                                        {expandedSections.monitoring ? <ChevronUp className="w-5 h-5 text-white/40" /> : <ChevronDown className="w-5 h-5 text-white/40" />}
                                    </button>
                                    {expandedSections.monitoring && viewingUser.monitoringRules.length > 0 && (
                                        <div className="p-4 pt-0 space-y-2">
                                            {viewingUser.monitoringRules.map(m => (
                                                <div key={m.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                                                    <div>
                                                        <p className="text-sm font-medium text-white">{m.name}</p>
                                                        <p className="text-xs text-white/40">{m.type}</p>
                                                    </div>
                                                    <span className={`px-2 py-0.5 text-xs rounded ${m.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                                        {m.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Files */}
                                <div className="rounded-lg bg-white/[0.02] border border-white/10 overflow-hidden">
                                    <button
                                        onClick={() => toggleSection('files')}
                                        className="w-full p-4 flex items-center justify-between hover:bg-white/5"
                                    >
                                        <div className="flex items-center gap-3">
                                            <FolderOpen className="w-5 h-5 text-yellow-400" />
                                            <span className="font-medium text-white">Files ({viewingUser.files?.length || 0})</span>
                                        </div>
                                        {expandedSections.files ? <ChevronUp className="w-5 h-5 text-white/40" /> : <ChevronDown className="w-5 h-5 text-white/40" />}
                                    </button>
                                    {expandedSections.files && viewingUser.files && viewingUser.files.length > 0 && (
                                        <div className="p-4 pt-0 space-y-2">
                                            {viewingUser.files.map(f => (
                                                <div key={f.name} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                                                    <div className="flex items-center gap-2">
                                                        <FileText className="w-4 h-4 text-white/40" />
                                                        <div>
                                                            <p className="text-sm text-white">{f.name}</p>
                                                            <p className="text-xs text-white/40">{(f.size / 1024).toFixed(1)} KB</p>
                                                        </div>
                                                    </div>
                                                    <span className="text-xs text-white/40 px-2 py-1 bg-white/5 rounded">{f.extension || 'file'}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Settings */}
                                <div className="rounded-lg bg-white/[0.02] border border-white/10 overflow-hidden">
                                    <button
                                        onClick={() => toggleSection('settings')}
                                        className="w-full p-4 flex items-center justify-between hover:bg-white/5"
                                    >
                                        <div className="flex items-center gap-3">
                                            <Settings className="w-5 h-5 text-blue-400" />
                                            <span className="font-medium text-white">Settings</span>
                                        </div>
                                        {expandedSections.settings ? <ChevronUp className="w-5 h-5 text-white/40" /> : <ChevronDown className="w-5 h-5 text-white/40" />}
                                    </button>
                                    {expandedSections.settings && (
                                        <div className="p-4 pt-0 space-y-3">
                                            {viewingUser.settings && Object.keys(viewingUser.settings).length > 0 ? (
                                                <div className="grid grid-cols-2 gap-3">
                                                    {viewingUser.settings.custom_script && (
                                                        <div className="col-span-2 p-3 bg-white/5 rounded-lg">
                                                            <p className="text-xs text-white/40 mb-1">Custom Script</p>
                                                            <code className="text-xs text-green-400 break-all">{viewingUser.settings.custom_script}</code>
                                                        </div>
                                                    )}
                                                    {viewingUser.settings.custom_class && (
                                                        <div className="p-3 bg-white/5 rounded-lg">
                                                            <p className="text-xs text-white/40 mb-1">Custom Class</p>
                                                            <p className="text-sm text-white font-mono">{viewingUser.settings.custom_class}</p>
                                                        </div>
                                                    )}
                                                    {viewingUser.settings.cf_email && (
                                                        <div className="p-3 bg-white/5 rounded-lg">
                                                            <p className="text-xs text-white/40 mb-1">Cloudflare Email</p>
                                                            <p className="text-sm text-white">{viewingUser.settings.cf_email}</p>
                                                        </div>
                                                    )}
                                                    {viewingUser.settings.cf_api_token && (
                                                        <div className="p-3 bg-white/5 rounded-lg">
                                                            <p className="text-xs text-white/40 mb-1">CF API Token</p>
                                                            <p className="text-sm text-white/60">{viewingUser.settings.cf_api_token}</p>
                                                        </div>
                                                    )}
                                                    {viewingUser.settings.bp_bearer_token && (
                                                        <div className="p-3 bg-white/5 rounded-lg">
                                                            <p className="text-xs text-white/40 mb-1">BP Bearer Token</p>
                                                            <p className="text-sm text-white/60">{viewingUser.settings.bp_bearer_token}</p>
                                                        </div>
                                                    )}
                                                    <div className="p-3 bg-white/5 rounded-lg">
                                                        <p className="text-xs text-white/40 mb-1">Notifications</p>
                                                        <p className="text-sm text-white">{viewingUser.settings.notifications_enabled ? 'Enabled' : 'Disabled'}</p>
                                                    </div>
                                                    <div className="p-3 bg-white/5 rounded-lg">
                                                        <p className="text-xs text-white/40 mb-1">Auto SSL</p>
                                                        <p className="text-sm text-white">{viewingUser.settings.auto_ssl ? 'Enabled' : 'Disabled'}</p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <p className="text-sm text-white/40 py-2">No settings configured</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Create/Edit Modal */}
            {(showCreateModal || editingUser) && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-sidebar-bg border border-border-default rounded-2xl p-8 w-full max-w-lg shadow-2xl">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold text-text-primary">
                                {editingUser ? 'Edit User' : 'Create New User'}
                            </h2>
                            <button
                                onClick={() => { setShowCreateModal(false); setEditingUser(null); resetForm(); }}
                                className="p-2 rounded-lg hover:bg-card-bg transition-colors text-text-muted hover:text-text-primary"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-text-muted mb-2">Username</label>
                                <input
                                    type="text"
                                    value={formData.username}
                                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                    className="w-full px-4 py-3 bg-card-bg border border-border-default rounded-xl text-text-primary focus:outline-none focus:border-accent-blue transition-colors"
                                    placeholder="Enter username"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-text-muted mb-2">
                                    Password {editingUser && <span className="text-text-muted/60">(leave empty to keep current)</span>}
                                </label>
                                <input
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    className="w-full px-4 py-3 bg-card-bg border border-border-default rounded-xl text-text-primary focus:outline-none focus:border-accent-blue transition-colors"
                                    placeholder="Enter password"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-text-muted mb-2">Role</label>
                                <select
                                    value={formData.role}
                                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                    className="w-full px-4 py-3 bg-card-bg border border-border-default rounded-xl text-text-primary focus:outline-none focus:border-accent-blue transition-colors"
                                >
                                    {ROLES.map(r => (
                                        <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-text-muted mb-2">Servers Limit</label>
                                    <input
                                        type="number"
                                        value={formData.servers_limit}
                                        onChange={(e) => setFormData({ ...formData, servers_limit: e.target.value })}
                                        placeholder="Default"
                                        className="w-full px-4 py-3 bg-card-bg border border-border-default rounded-xl text-text-primary focus:outline-none focus:border-accent-blue transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-text-muted mb-2">Domains Limit</label>
                                    <input
                                        type="number"
                                        value={formData.domains_limit}
                                        onChange={(e) => setFormData({ ...formData, domains_limit: e.target.value })}
                                        placeholder="Default"
                                        className="w-full px-4 py-3 bg-card-bg border border-border-default rounded-xl text-text-primary focus:outline-none focus:border-accent-blue transition-colors"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-text-muted mb-2">Subscription Expires</label>
                                <input
                                    type="date"
                                    value={formData.subscription_expires}
                                    onChange={(e) => setFormData({ ...formData, subscription_expires: e.target.value })}
                                    className="w-full px-4 py-3 bg-card-bg border border-border-default rounded-xl text-text-primary focus:outline-none focus:border-accent-blue transition-colors"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 mt-8">
                            <button
                                onClick={() => { setShowCreateModal(false); setEditingUser(null); resetForm(); }}
                                className="btn-secondary px-6"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={editingUser ? handleUpdate : handleCreate}
                                disabled={saving}
                                className="btn-primary px-6 flex items-center space-x-2"
                            >
                                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                                <span>{editingUser ? 'Update' : 'Create'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading Overlay */}
            {loadingDetails && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="text-center">
                        <Loader2 className="w-12 h-12 animate-spin text-accent-blue mx-auto mb-4" />
                        <p className="text-text-muted">Loading user details...</p>
                    </div>
                </div>
            )}

            {/* Transfer Modal */}
            {showTransferModal && transferFromId && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-sidebar-bg border border-border-default rounded-2xl p-6 w-full max-w-md shadow-2xl">
                        <h2 className="text-xl font-bold text-text-primary mb-4">Transfer Data</h2>
                        <p className="text-text-muted text-sm mb-4">
                            Transfer all servers, domains and pages from <strong className="text-text-primary">{users.find(u => u.id === transferFromId)?.username}</strong> to another user:
                        </p>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-text-muted mb-2">Transfer To</label>
                            <select
                                value={transferToId}
                                onChange={(e) => setTransferToId(e.target.value)}
                                className="w-full px-4 py-3 bg-card-bg border border-border-default rounded-xl text-text-primary focus:outline-none focus:border-accent-blue"
                            >
                                <option value="">Select user...</option>
                                {users.filter(u => u.id !== transferFromId).map(u => (
                                    <option key={u.id} value={u.id}>{u.username} ({u.role})</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => { setShowTransferModal(false); setTransferFromId(null); setTransferToId(''); }}
                                className="btn-secondary px-4"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleTransfer}
                                disabled={!transferToId || transferring}
                                className="btn-primary px-4 flex items-center space-x-2 disabled:opacity-50"
                            >
                                {transferring ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                <span>Transfer</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersPanel;
